import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiLink, FiLock } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { toInputDate } from '../../lib/format';
import { archiveDocuments, documentError, sendFiles, storedDocuments } from '../../lib/documents';
import AttachmentsField, { useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentViewer from '../documents/DocumentViewer';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Field, MultiChips, Notice, PageHeader, Panel, Segmented, Select, SIZE, SkeletonRows, TextArea, TextInput } from '../ui/kit';
import PatientPicker from '../patients/PatientPicker';
import { ECHO_SECTIONS, GROUPS, bodySurface, echoAccess, fromPatient, likelyPatients, patientName, valueOf } from './echoModel';

const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => {
  const values = Object.fromEntries(GROUPS.map((group) => [group, {}]));
  ECHO_SECTIONS.forEach((section) =>
    section.fields.forEach((field) => {
      values[section.group][field.name] = field.type === 'multi' ? [] : '';
    })
  );
  return values;
};

const toValues = (echo) => {
  const values = emptyValues();
  ECHO_SECTIONS.forEach((section) =>
    section.fields.forEach((field) => {
      const value = valueOf(echo, section.group, field.name);
      if (field.type === 'multi') values[section.group][field.name] = (Array.isArray(value) ? value : [value]).filter(Boolean);
      else if (field.type === 'date') values[section.group][field.name] = toInputDate(value);
      else values[section.group][field.name] = String(value ?? '');
    })
  );
  return values;
};

const EchoForm = () => {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const user = useSessionUser();
  const access = echoAccess(user);
  const { patients, ensurePatients } = usePatientDoctorContext();

  const [status, setStatus] = useState(editing ? 'loading' : 'ready');
  const [original, setOriginal] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [patientId, setPatientId] = useState('');
  const [dateExamen, setDateExamen] = useState(today());
  const [files, setFiles] = useState([]); // former Dropbox links { nom, lien }
  // Files of the service's Dropbox, files chosen on the device (sent on save), documents taken out.
  const [documents, setDocuments] = useState([]);
  const [pending, setPending] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [sending, setSending] = useState(null);
  const [viewing, setViewing] = useState(null);
  const documentsStatus = useDocumentsStatus();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const surfaceAuto = useRef(true);

  useEffect(() => {
    ensurePatients().catch(() => {});
    if (!editing) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'echos', id))
      .then((snapshot) => {
        if (cancelled) return;
        if (!snapshot.exists()) {
          setStatus('missing');
          return;
        }
        const echo = { id: snapshot.id, ...snapshot.data() };
        setOriginal(echo);
        setValues(toValues(echo));
        setPatientId(echo.patientId || '');
        setDateExamen(toInputDate(echo.dateExamen || echo.dateCreation) || today());
        setFiles(Array.isArray(echo.fichiers) ? echo.fichiers : []);
        setDocuments(storedDocuments(echo.documents));
        surfaceAuto.current = !valueOf(echo, 'identite', 'surfaceCorporelle');
        setStatus('ready');
      })
      .catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [editing, id, ensurePatients]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const setField = (group, name, value) => {
    setDirty(true);
    setError('');
    setValues((current) => {
      const next = { ...current, [group]: { ...current[group], [name]: value } };
      if (group === 'identite' && name === 'surfaceCorporelle') surfaceAuto.current = !value;
      if (group === 'identite' && (name === 'poids' || name === 'taille') && surfaceAuto.current) {
        next.identite.surfaceCorporelle = bodySurface(next.identite.poids, next.identite.taille);
      }
      return next;
    });
  };

  // Linking a file fills the identity fields that are still empty.
  const pickPatient = (patient) => {
    setDirty(true);
    setValues((current) => {
      const filledFromFile = fromPatient(patient);
      const identite = { ...current.identite };
      Object.entries(filledFromFile).forEach(([name, value]) => {
        if (value && !String(identite[name] || '').trim()) identite[name] = value;
      });
      if (surfaceAuto.current && !identite.surfaceCorporelle) identite.surfaceCorporelle = bodySurface(identite.poids, identite.taille);
      return { ...current, identite };
    });
  };

  const suggestions = useMemo(
    () => (patientId || !original ? [] : likelyPatients(original, patients)),
    [patientId, original, patients]
  );

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!patientId && !String(values.identite.nom || '').trim() && !String(values.identite.prenom || '').trim()) {
      setError('Rattachez l’écho à un dossier patient ou saisissez au moins le nom du patient.');
      return;
    }
    setSaving(true);
    setError('');
    const identite = { ...values.identite, fc: values.identite.FC };
    let stored = documents;
    try {
      // Files sent first: once in the service's Dropbox, they stay listed if a later step fails.
      if (pending.length) {
        try {
          stored = [...stored, ...(await sendFiles(pending, { section: 'echo', ...(patientId ? { patientId } : {}) }, setSending))];
          setPending([]);
          setDocuments(stored);
        } catch (sendError) {
          const sent = sendError.sent || [];
          stored = [...stored, ...sent];
          setDocuments(stored);
          setPending((current) => current.slice(sent.length));
          throw sendError;
        } finally {
          setSending(null);
        }
      }
      if (removed.length) {
        const failed = await archiveDocuments(removed);
        setRemoved([]);
        if (failed.length) {
          stored = [...stored, ...failed];
          setDocuments(stored);
          throw new Error('archive');
        }
      }
      const payload = {
        ...values,
        identite,
        fichiers: files,
        documents: stored.map(({ id: documentId, name, mimeType }) => ({ id: documentId, name, mimeType: mimeType || '' })),
        patientId: patientId || null,
        dateExamen,
      };
      if (editing) {
        await updateDoc(doc(db, 'echos', id), { ...payload, dateModification: new Date().toISOString(), updatedBy: user.name || '' });
        setDirty(false);
        navigate(`/echo/${id}`, { replace: true, state: { saved: true } });
      } else {
        const created = await addDoc(collection(db, 'echos'), { ...payload, dateCreation: new Date().toISOString(), createdBy: user.name || '' });
        setDirty(false);
        navigate(`/echo/${created.id}`, { replace: true, state: { saved: true } });
      }
    } catch (err) {
      setError(
        err.message === 'archive'
          ? 'Un document n’a pas pu être retiré. Réessayez.'
          : await documentError(err, 'L’échographie n’a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.')
      );
    } finally {
      setSaving(false);
    }
  };

  // Taking a stored document out of the report sends it to the archives: deletion level.
  const removeStored = (item) => {
    setDirty(true);
    setDocuments((current) => current.filter((entry) => entry.id !== item.id));
    setRemoved((current) => [...current, item]);
  };

  const cancel = () => {
    if (dirty && !window.confirm('Abandonner les modifications non enregistrées ?')) return;
    setDirty(false);
    navigate(editing ? `/echo/${id}` : '/echo');
  };

  const back = editing ? { to: `/echo/${id}`, label: 'Retour à l’échographie' } : { to: '/echo', label: 'Échographies' };
  const shell = (content) => <main className="mx-auto max-w-5xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">{content}</main>;

  if (!access.canWrite) {
    return shell(
      <>
        <PageHeader back={back} title="Saisie impossible" />
        <div className={`${CARD} mt-8`}>
          <EmptyState icon={FiLock} title="Les comptes rendus d’écho sont saisis par la Cardiologie.">
            Vos droits permettent de les consulter. Demandez à l’administrateur si vous devez en saisir.
          </EmptyState>
        </div>
      </>
    );
  }
  if (status !== 'ready') {
    return shell(
      <>
        <PageHeader back={back} title={status === 'loading' ? 'Chargement…' : 'Échographie introuvable'} />
        <div className={`${CARD} mt-8 ${status === 'loading' ? 'p-6' : ''}`}>
          {status === 'loading' ? <SkeletonRows rows={4} /> : <EmptyState icon={FiAlertCircle} title="L’échographie n’a pas pu être chargée." />}
        </div>
      </>
    );
  }

  const renderField = (section, field) => {
    const fieldId = `echo-${section.group}-${field.name}`;
    const value = values[section.group][field.name];
    const onValue = (next) => setField(section.group, field.name, next);
    let input;
    if (field.type === 'textarea') input = <TextArea id={fieldId} value={value} onChange={(e) => onValue(e.target.value)} />;
    else if (field.type === 'select') input = <Select id={fieldId} value={value} onChange={(e) => onValue(e.target.value)} options={field.options} placeholder="—" />;
    else if (field.type === 'segmented') input = <Segmented id={fieldId} value={value} onChange={onValue} options={field.options} />;
    else if (field.type === 'multi') input = <MultiChips id={fieldId} options={field.options} values={value} onChange={onValue} customPlaceholder="Autre opérateur…" />;
    else {
      input = (
        <div className="relative">
          <TextInput
            id={fieldId}
            type={field.type === 'date' ? 'date' : field.inputType || 'text'}
            step={field.step}
            inputMode={field.inputType === 'number' ? 'decimal' : undefined}
            value={value}
            onChange={(e) => onValue(e.target.value)}
            placeholder={field.placeholder}
            className={field.unit ? 'pr-[64px]' : ''}
          />
          {field.unit && (
            <span className="pointer-events-none absolute right-[14px] top-1/2 -translate-y-1/2 text-[13px] text-black/35">{field.unit}</span>
          )}
        </div>
      );
    }
    const wide = field.wide || field.type === 'textarea' || field.type === 'multi';
    return (
      <div key={field.name} className={wide ? 'col-span-2 sm:col-span-3' : ''}>
        <Field id={fieldId} label={field.label} hint={field.hint}>
          {input}
        </Field>
      </div>
    );
  };

  const linked = patients.find((patient) => patient._id === patientId);

  return shell(
    <form onSubmit={submit} noValidate>
      <PageHeader
        back={back}
        eyebrow={editing ? 'Modifier l’échographie' : 'Nouvelle échographie'}
        title={editing ? patientName({ identite: values.identite }) : 'Compte rendu d’écho-doppler'}
        subtitle="Seuls les champs remplis apparaîtront dans le compte rendu."
      />

      <nav aria-label="Sections du compte rendu" className="sticky top-14 z-10 -mx-[20px] mt-6 flex gap-1.5 overflow-x-auto bg-mist/90 px-[20px] py-2 backdrop-blur-xl [scrollbar-width:none] sm:-mx-8 sm:px-8 lg:top-0 lg:-mx-10 lg:px-10 [&::-webkit-scrollbar]:hidden">
        {[{ id: 'rattachement', title: 'Dossier' }, ...ECHO_SECTIONS, { id: 'documents', title: 'Documents' }].map((section) => (
          <a
            key={section.id}
            href={`#section-${section.id}`}
            className="inline-flex h-8 flex-none items-center whitespace-nowrap rounded-full bg-white px-[12px] text-[13px] font-medium text-black/60 no-underline shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink"
          >
            {section.title}
          </a>
        ))}
      </nav>

      <div className="mt-[16px] flex flex-col gap-[16px]">
        <Panel id="section-rattachement" className="scroll-mt-[120px]" title="Dossier patient et date" description="Rattacher l’écho au dossier la fait apparaître dans la fiche du patient et remplit l’identité.">
          <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-[1fr_220px]">
            <Field id="echo-patient" label="Dossier patient">
              <PatientPicker id="echo-patient" single values={patientId ? [patientId] : []} onChange={(ids) => { setDirty(true); setPatientId(ids[0] || ''); }} onPick={pickPatient} />
            </Field>
            <Field id="echo-date" label="Date de l’examen">
              <TextInput id="echo-date" type="date" value={dateExamen} onChange={(e) => { setDirty(true); setDateExamen(e.target.value); }} />
            </Field>
          </div>
          {!linked && suggestions.length > 0 && (
            <div className="mt-[14px]">
              <Notice tone="brand" icon={FiLink}>
                <span>Dossier probable (même nom) : </span>
                {suggestions.slice(0, 3).map((patient) => (
                  <button
                    key={patient._id}
                    type="button"
                    onClick={() => {
                      setDirty(true);
                      setPatientId(patient._id);
                      pickPatient(patient);
                    }}
                    className="ml-1 inline-flex h-7 cursor-pointer items-center rounded-full border-0 bg-white px-[10px] text-[13px] font-semibold text-brand-700 shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:bg-brand-100"
                  >
                    Rattacher à {patient.nom}
                    {patient.dossierNumber ? ` (${patient.dossierNumber})` : ''}
                  </button>
                ))}
              </Notice>
            </div>
          )}
        </Panel>

        {ECHO_SECTIONS.map((section) => (
          <Panel key={section.id} id={`section-${section.id}`} className="scroll-mt-[120px]" title={section.title}>
            <div className="grid grid-cols-2 gap-[16px] sm:grid-cols-3">{section.fields.map((field) => renderField(section, field))}</div>
          </Panel>
        ))}

        <Panel id="section-documents" className="scroll-mt-[120px]" title="Documents">
          <AttachmentsField
            id="echo-documents"
            status={documentsStatus}
            stored={documents}
            onOpen={setViewing}
            onRemoveStored={access.canDelete && !saving ? removeStored : null}
            links={files.map((file, index) => ({ link: file.lien, name: file.nom || `Document ${index + 1}` }))}
            onLinksChange={
              saving
                ? null
                : (next) => {
                    setDirty(true);
                    setFiles(next.map((item) => ({ nom: item.name, lien: item.link })));
                  }
            }
            pending={pending}
            onPendingChange={(next) => {
              setDirty(true);
              setPending(next);
            }}
            sending={sending}
          />
          {removed.length > 0 && (
            <p className="mb-0 mt-[12px] text-[12.5px] text-black/45">
              {removed.length === 1 ? 'Un document sera retiré' : `${removed.length} documents seront retirés`} à l’enregistrement (conservés dans les archives du
              Dropbox du service).
            </p>
          )}
        </Panel>

        {viewing && <DocumentViewer item={viewing} onClose={() => setViewing(null)} canReplace={access.canWrite} canDownload={access.canExport} />}
      </div>

      {error && (
        <div className="mt-6">
          <Notice tone="red" icon={FiAlertCircle}>
            {error}
          </Notice>
        </div>
      )}

      <div className="sticky bottom-0 z-10 -mx-[20px] mt-8 border-0 border-t-[1px] border-solid border-black/[0.06] bg-mist/90 px-[20px] py-[14px] backdrop-blur-xl sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {dirty && <span className="text-center text-[13px] text-black/45 sm:mr-auto sm:text-left">Modifications non enregistrées</span>}
          <button type="button" onClick={cancel} className={`${BUTTON.secondary} ${SIZE.md}`}>
            Annuler
          </button>
          <button type="submit" disabled={saving} className={`${BUTTON.primary} ${SIZE.md} min-w-[180px]`}>
            {sending
              ? `Envoi des documents ${sending.index + 1}/${pending.length}…`
              : saving
                ? 'Enregistrement…'
                : editing
                  ? 'Enregistrer les modifications'
                  : 'Enregistrer l’échographie'}
          </button>
        </div>
      </div>
      {!editing && (
        <p className="mb-0 mt-[12px] text-center text-[12.5px] text-black/40">
          <Link to="/echo" className="text-black/50 underline hover:text-ink">
            Retour à la liste sans enregistrer
          </Link>
        </p>
      )}
    </form>
  );
};

export default EchoForm;
